import type { PipelineBoard, PipelineCard, PipelineKind } from "@agency/core";
import { DndContext, DragOverlay, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from "@dnd-kit/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GripVertical, LayoutList, Columns3 } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { useToast } from "../components/Toast";
import { Badge, Card, EmptyState, ErrorState, LoadingState, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, relativeDays, shortDate } from "../theme";

type BoardCard = PipelineBoard["stages"][number]["cards"][number];
const KINDS: PipelineKind[] = ["sales", "renewal", "service", "cross_sell"];

export function PipelinesScreen() {
  const { kind: kindParam } = useParams();
  const kind = (KINDS.includes(kindParam as PipelineKind) ? kindParam : "sales") as PipelineKind;
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [view, setView] = useState<"board" | "list">("board");
  const [active, setActive] = useState<BoardCard | null>(null);
  const key = ["pipeline", kind];
  const q = useQuery({ queryKey: key, queryFn: async () => (await callAction<"agency.pipeline.list", PipelineBoard>("agency.pipeline.list", { pipeline: kind })).result });

  const move = useMutation({
    mutationFn: (v: { cardId: string; toStage: string; toIndex?: number }) => callAction<"agency.pipeline.move", PipelineCard>("agency.pipeline.move", { cardId: v.cardId, toStage: v.toStage, toIndex: v.toIndex ?? 0 }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<PipelineBoard>(key);
      if (prev) qc.setQueryData<PipelineBoard>(key, applyMove(prev, v.cardId, v.toStage));
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast({ title: t("common.error"), body: (e as Error).message, tone: "error" });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });

  const doMove = (card: BoardCard, toStage: string) => {
    if (card.stage === toStage) return;
    const from = card.stage;
    move.mutate({ cardId: card.id, toStage });
    toast({ title: t("pipeline.moved"), body: `${card.title} → ${stageLabel(q.data, toStage, locale)}`, onUndo: () => move.mutateAsync({ cardId: card.id, toStage: from }) });
    if (kind === "renewal" && config.agency.features.gamification) toast({ title: t("renewals.niceTouch"), durationMs: 2500 });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragStart = (e: DragStartEvent) => setActive((e.active.data.current as { card: BoardCard } | undefined)?.card ?? null);
  const onDragEnd = (e: DragEndEvent) => {
    setActive(null);
    const card = (e.active.data.current as { card: BoardCard } | undefined)?.card;
    const toStage = e.over?.id as string | undefined;
    if (card && toStage) doMove(card, toStage);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-bold mr-auto">{t("nav.pipelines")}</h1>
        <div className="flex rounded-xl border border-ink/15 overflow-hidden">
          <button onClick={() => setView("board")} aria-pressed={view === "board"} className={cn("min-h-[40px] px-3 text-sm font-semibold inline-flex items-center gap-1", view === "board" ? "bg-ink text-white" : "hover:bg-ink/5")}>
            <Columns3 className="h-4 w-4" /> {t("common.view.board")}
          </button>
          <button onClick={() => setView("list")} aria-pressed={view === "list"} className={cn("min-h-[40px] px-3 text-sm font-semibold inline-flex items-center gap-1", view === "list" ? "bg-ink text-white" : "hover:bg-ink/5")}>
            <LayoutList className="h-4 w-4" /> {t("common.view.list")}
          </button>
        </div>
      </div>
      <div className="flex gap-1 overflow-x-auto scrollbar-thin">
        {KINDS.map((k) => (
          <NavLink key={k} to={`/pipelines/${k}`} className={({ isActive }) => cn("min-h-tap whitespace-nowrap rounded-xl px-4 inline-flex items-center text-sm font-semibold", isActive ? "bg-primary text-white" : "bg-white border border-ink/10 hover:bg-ink/5")}>
            {t(`pipeline.${k}`)}
          </NavLink>
        ))}
      </div>

      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      {q.data && view === "board" ? (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto scrollbar-thin pb-3 -mx-4 px-4 md:mx-0 md:px-0">
            {q.data.stages.map((s, i) => (
              <StageColumn key={s.id} stage={s} index={i} board={q.data!} onMove={doMove} />
            ))}
          </div>
          <DragOverlay>{active ? <CardView card={active} board={q.data} onMove={() => {}} overlay /> : null}</DragOverlay>
        </DndContext>
      ) : null}
      {q.data && view === "list" ? (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-ink/55">
              <tr>
                <th className="px-4 py-3">{t("common.name")}</th>
                <th className="px-4 py-3">{t("common.status")}</th>
                <th className="px-4 py-3">{t("common.premium")}</th>
                <th className="px-4 py-3">{t("common.due")}</th>
                <th className="px-4 py-3">{t("pipeline.moveTo")}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.stages.flatMap((s) =>
                s.cards.map((c) => (
                  <tr key={c.id} className={cn("border-t border-ink/5", c.rotting && "bg-accent/10")}>
                    <td className="px-4 py-2 font-semibold">
                      <Link to={`/customers/${c.customerId}`} className="hover:underline">{c.title}</Link>
                    </td>
                    <td className="px-4 py-2">{s.label[locale]}</td>
                    <td className="px-4 py-2">{money(c.valueCents)}</td>
                    <td className="px-4 py-2">{c.dueAt ? shortDate(c.dueAt, locale) : "—"}</td>
                    <td className="px-4 py-2"><MoveSelect card={c} board={q.data!} onMove={doMove} /></td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </Card>
      ) : null}
    </div>
  );
}

function StageColumn({ stage, index, board, onMove }: { stage: PipelineBoard["stages"][number]; index: number; board: PipelineBoard; onMove: (c: BoardCard, to: string) => void }) {
  const { locale } = useLocale();
  const t = useT();
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  return (
    <div className="w-[272px] shrink-0 flex flex-col">
      <div className={cn("chevron-stage px-5 py-2 text-white font-heading uppercase text-sm tracking-wide flex items-center justify-between", index === 0 ? "bg-primary" : "bg-ink")}>
        <span className="truncate">{stage.label[locale]}</span>
        <span className="ml-2 rounded-full bg-white/20 px-2 text-xs">{stage.cards.length}</span>
      </div>
      <div ref={setNodeRef} className={cn("mt-2 flex-1 min-h-[160px] space-y-2 rounded-2xl p-2 transition-colors", isOver ? "bg-primary/10 ring-2 ring-primary/40" : "bg-ink/4")}>
        {stage.cards.length === 0 ? <p className="px-2 py-6 text-center text-xs text-ink/45">{t("pipeline.empty")}</p> : null}
        {stage.cards.map((c) => (
          <DraggableCard key={c.id} card={c} board={board} onMove={onMove} />
        ))}
      </div>
    </div>
  );
}

function DraggableCard({ card, board, onMove }: { card: BoardCard; board: PipelineBoard; onMove: (c: BoardCard, to: string) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id, data: { card } });
  return (
    <div ref={setNodeRef} className={cn(isDragging && "opacity-40")}>
      <CardView card={card} board={board} onMove={onMove} handleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

function CardView({ card, board, onMove, handleProps, overlay }: { card: BoardCard; board: PipelineBoard; onMove: (c: BoardCard, to: string) => void; handleProps?: Record<string, unknown>; overlay?: boolean }) {
  const t = useT();
  const { locale } = useLocale();
  return (
    <div className={cn("rounded-xl bg-white border p-3 shadow-sm", card.rotting ? "border-accent/60 bg-accent/10" : "border-ink/8", overlay && "shadow-2xl rotate-1")}>
      <div className="flex items-start gap-2">
        <button {...handleProps} aria-label="drag" className="min-h-[40px] min-w-[28px] -ml-1 inline-flex items-center justify-center text-ink/30 cursor-grab active:cursor-grabbing touch-none">
          <GripVertical className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <Link to={`/customers/${card.customerId}`} className="font-semibold text-sm hover:underline block truncate">{card.title}</Link>
          <p className="text-xs text-ink/55 mt-0.5">
            {card.valueCents != null ? `${money(card.valueCents)} · ` : ""}
            {card.dueAt ? `${t("common.due")} ${shortDate(card.dueAt, locale)}` : relativeDays(-card.daysSinceTouch, locale)}
          </p>
          {card.rotting ? <Badge tone="amber" className="mt-1">{t("pipeline.rotting")} · {card.daysSinceTouch}d</Badge> : null}
        </div>
      </div>
      {!overlay ? <div className="mt-2"><MoveSelect card={card} board={board} onMove={onMove} /></div> : null}
    </div>
  );
}

function MoveSelect({ card, board, onMove }: { card: BoardCard; board: PipelineBoard; onMove: (c: BoardCard, to: string) => void }) {
  const t = useT();
  const { locale } = useLocale();
  return (
    <select
      aria-label={t("pipeline.moveTo")}
      value={card.stage}
      onChange={(e) => onMove(card, e.target.value)}
      className="w-full min-h-[36px] rounded-lg border border-ink/10 bg-white px-2 text-xs font-semibold text-ink/70"
    >
      {board.stages.map((s) => (
        <option key={s.id} value={s.id}>{s.label[locale]}</option>
      ))}
    </select>
  );
}

function applyMove(board: PipelineBoard, cardId: string, toStage: string): PipelineBoard {
  let moved: BoardCard | undefined;
  const stages = board.stages.map((s) => {
    const idx = s.cards.findIndex((c) => c.id === cardId);
    if (idx >= 0) {
      moved = { ...s.cards[idx]!, stage: toStage, lastTouchedAt: new Date().toISOString(), rotting: false, daysSinceTouch: 0 };
      return { ...s, cards: s.cards.filter((c) => c.id !== cardId) };
    }
    return s;
  });
  return { ...board, stages: stages.map((s) => (s.id === toStage && moved ? { ...s, cards: [moved, ...s.cards] } : s)) };
}

function stageLabel(board: PipelineBoard | undefined, id: string, locale: "en" | "es") {
  return board?.stages.find((s) => s.id === id)?.label[locale] ?? id;
}

export function PipelinesEmpty() {
  const t = useT();
  return <EmptyState title={t("common.empty")} />;
}
